import { inject, Injectable } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { LocationObject } from 'src/app/swagger-api';
import AppValues from '../common/app.values';
import { CountryInfo } from '../interfaces/country-info.interface';
import { Attribute } from '../interfaces/form-and-field.interface';
import { PhoneNumberService } from '../services/phone-number.service';
import { MultipleComplexValidator } from './fields/components/multiple-complex-search-form-field/Validators/multiple-complex.validator';
import { FieldCustomValidators } from './fields/field-custom.validators';

@Injectable()
export class FormValidationService {
  private readonly errorValidationMessages: { [key: string]: string } = {
    required: 'FIELD_REQUIRED_ERROR',
    requiredTrue: 'FIELD_REQUIRED_ERROR',
    email: 'FIELD_EMAIL_ERROR',
    accountType: 'FIELD_ACCOUNT_TYPE_ERROR',
    password: 'FIELD_PASSWORD_ERROR',
    alias: 'FIELD_ALIAS_ERROR',
    organizationName: 'FIELD_ORGANIZATION_NAME_ERROR',
    findPasswordRegEx: 'REPEAT_PASSWORD_INPUT_ERROR',
    alphabetic: 'FIELD_ALPHABETIC_ERROR',
    alphabeticName: 'FIELD_ALPHABETIC_NAME_ERROR',
    zip_code: 'FIELD_ZIP_CODE_ERROR',
    alphanumeric: 'FIELD_ALPHANUMERIC_ERROR',
    url: 'FIELD_URL_ERROR',
    date: 'FIELD_DATE_ERROR',
    startDate: 'FIELD_START_DATE_ERROR',
    endDate: 'FIELD_END_DATE_ERROR',
    notExistingDate: 'FIELD_DATE_NOT_EXIST_ERROR',
    yearLessThenAllowed: 'FIELD_LESS_THEN_1900',
    phone: 'FIELD_PHONE_ERROR',
    verification_code: 'FIELD_VERIFICATION_CODE_ERROR',
    notRequiredFileType: 'FIELD_REQUIRED_FILE_TYPE_ERROR',
    notValidMultipleFiles: 'FIELD_MULTIPLE_FILES_ERROR',
    notRequiredPhotoType: 'FIELD_REQUIRED_PHOTO_TYPE_ERROR',
    minlength: 'FIELD_MIN_LENGTH_ERROR',
    maxlength: 'FIELD_MAX_LENGTH_ERROR',
    multiple_select_badge: 'FIELD_SINGLE_SELECT_BADGE_ERROR',
    single_select_badge: 'FIELD_SINGLE_SELECT_BADGE_ERROR',
    city: 'FIELD_LOCATION_CITY_ERROR',
    street: 'FIELD_LOCATION_STREET_ERROR',
    fieldIsEmpty: 'FIELD_RICH_EDITOR_EMPTY_ERROR',
    passwordsNotMatch: 'REPEAT_PASSWORD_INPUT_ERROR',
    dynamicMaxLength: 'FIELD_MULTIPLE_SEARCH_MAX_LENGTH_ERROR',
  };

  private phoneNumberService = inject(PhoneNumberService);

  public getValidationError(control: AbstractControl): string {
    let error = '';
    if (this.isInvalid(control)) {
      const errorName: string = Object.keys(control.errors)[0];
      error = this.errorValidationMessages[errorName] || '';
    }
    return error;
  }

  public isInvalid(control: AbstractControl): boolean {
    return control?.invalid && (control.dirty || control.touched);
  }

  public getFormPasswordValidators(): ValidationErrors | null {
    return FieldCustomValidators.passwordMatches;
  }

  public getPhoneValidators(countryInfo: CountryInfo): ValidatorFn[] {
    return [
      FieldCustomValidators.patternValidator(AppValues.noSymbolsPattern, { phone: true }),
      FieldCustomValidators.phoneValidator(countryInfo, this.phoneNumberService),
    ];
  }

  public cityValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if ((control.value as LocationObject) && (control.value as LocationObject).city === '') {
        return { city: true };
      }
      return null;
    };
  }

  public streetValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if ((control.value as LocationObject) && (control.value as LocationObject).street === '') {
        return { street: true };
      }
      return null;
    };
  }

  public changeValidators(control: UntypedFormControl, validators: ValidatorFn[]): void {
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  public checkMultipleSearchFieldValidationErrors(control: UntypedFormControl, value: string): void {
    FormValidationService.checkSearchFieldValidationErrors(control, value, 'multiple_select_badge');
  }

  public checkSingleSearchFieldErrors(control: UntypedFormControl, value: string): void {
    FormValidationService.checkSearchFieldValidationErrors(control, value, 'single_select_badge');
  }

  public getSearchMultipleFieldValidationErrors(
    value: string,
    backendAttribute: Attribute,
  ): { [p: string]: boolean } | null {
    if (!value.trim()) {
      return { ['required']: true };
    }
    if (backendAttribute.max_length < value.trim().length) {
      return { ['maxlength']: true };
    }
    return null;
  }

  private static checkSearchFieldValidationErrors(control: UntypedFormControl, value: string, errorName: string): void {
    value.trim() ? control.setErrors(value.trim() ? { [errorName]: true } : null) : control.updateValueAndValidity();

    control.markAllAsTouched();
  }

  public checkMultipleComplexValidationErrors(
    control: UntypedFormControl,
    validators: Array<MultipleComplexValidator>,
  ): void {
    for (const validator of validators) {
      if (!validator.isValid) {
        control.setErrors({ [validator.errorKey]: true });
        control.markAllAsTouched();
        return;
      }
    }

    control.setErrors(null);
    control.markAllAsTouched();
  }
}
